import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import MobileFormFooter from '../common/MobileFormFooter';
import { useAuth } from '../../contexts/AuthContext';

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  area: Yup.string().nullable(),
  registration_number: Yup.string().nullable(),
  contact_info: Yup.string().nullable(),
  latitude: Yup.number().nullable().min(-90, 'Latitude must be ≥ -90').max(90, 'Latitude must be ≤ 90'),
  longitude: Yup.number().nullable().min(-180, 'Longitude must be ≥ -180').max(180, 'Longitude must be ≤ 180'),
});

const TenantForm = ({ initialValues, onSubmit, onCancel, isSubmitting, error }) => {
  const { isSuperAdmin } = useAuth();
  const canSetShadow = isSuperAdmin();

  // Initialize form with Formik
  const formik = useFormik({
    initialValues: initialValues || {
      name: '',
      area: '',
      registration_number: '',
      contact_info: '',
      latitude: '',
      longitude: '',
      is_shadow: false,
    },
    validationSchema,
    onSubmit,
  });

  return (
    <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        Tenant Information
      </Typography>
      
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="name"
              name="name"
              label="Tenant Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="area"
              name="area"
              label="Area"
              value={formik.values.area}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.area && Boolean(formik.errors.area)}
              helperText={formik.touched.area && formik.errors.area}
              placeholder="e.g., North Region, South Africa"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="registration_number"
              name="registration_number"
              label="Registration Number"
              value={formik.values.registration_number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.registration_number && Boolean(formik.errors.registration_number)}
              helperText={formik.touched.registration_number && formik.errors.registration_number}
              placeholder="e.g., REG123456"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="contact_info"
              name="contact_info"
              label="Contact Information"
              value={formik.values.contact_info}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.contact_info && Boolean(formik.errors.contact_info)}
              helperText={formik.touched.contact_info && formik.errors.contact_info}
              placeholder="e.g., Email: contact@example.com, Phone: +27 123 456 789"
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="latitude"
              name="latitude"
              label="Latitude"
              type="number"
              inputProps={{ step: 'any' }}
              value={formik.values.latitude ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.latitude && Boolean(formik.errors.latitude)}
              helperText={(formik.touched.latitude && formik.errors.latitude) || 'Decimal degrees, e.g. -25.624'}
              placeholder="-25.624739"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="longitude"
              name="longitude"
              label="Longitude"
              type="number"
              inputProps={{ step: 'any' }}
              value={formik.values.longitude ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.longitude && Boolean(formik.errors.longitude)}
              helperText={(formik.touched.longitude && formik.errors.longitude) || 'Decimal degrees, e.g. 28.359'}
              placeholder="28.359040"
            />
          </Grid>
          
          {/* Super admin only. The backend drops the field for everyone else
              rather than rejecting the request, so hiding it here is a UI
              nicety and not the actual gate. */}
          {canSetShadow && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="is_shadow"
                    checked={Boolean(formik.values.is_shadow)}
                    onChange={formik.handleChange}
                  />
                }
                label="Shadow FPA (record-keeping only)"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                For associations that do not use this system. They get no users
                and no login, stay hidden from the FPA switcher, and can still be
                credited when they assist on a fire. Promote one later from the
                FPA list — the record keeps everything already logged against it.
              </Typography>
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', gap: 2 }}>
              {onCancel && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onCancel}
                  startIcon={<CancelIcon />}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={isSubmitting ? <CircularProgress size={24} /> : <SaveIcon />}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <MobileFormFooter
          onCancel={onCancel}
          isSubmitting={isSubmitting}
          saveLabel="Save Tenant"
        />
      </form>
    </Paper>
  );
};

export default TenantForm;